import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, BookOpen, ArrowRight, CheckCircle, ShoppingCart, Sparkles, Target, Clock, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Kajabi purchase link
const KAJABI_PURCHASE_URL = "https://vittafinance.mykajabi.com/offers/2z27ZUWJ";

const MOCK_EXAM_TILES = [
  { id: 'mock-exam-1', title: 'Mock Exam 1', description: '110 comprehensive questions with detailed rationales for every answer', icon: 'EDIT_ICON', color: '#22d3ee' },
  { id: 'mock-exam-2', title: 'Mock Exam 2', description: '110 comprehensive questions with detailed rationales for every answer', icon: 'CLIPBOARD_ICON', color: '#34d399' },
  { id: 'mock-exam-3', title: 'Mock Exam 3', description: '110 comprehensive questions with detailed rationales for every answer', icon: 'FILE_ICON', color: '#fbbf24' },
  { id: 'mock-exam-4', title: 'Mock Exam 4', description: '110 comprehensive questions with detailed rationales for every answer', icon: 'FILE2_ICON', color: '#f87171' },
  { id: 'mock-exam-5', title: 'Mock Exam 5', description: '110 comprehensive questions with detailed rationales for every answer', icon: 'FILE3_ICON', color: '#a78bfa' },
];

const CIREMockExamPackSalesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get('token');
  
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const PRICE = 49.99;

  // Check access on mount if token is in URL
  useEffect(() => {
    if (tokenFromUrl) {
      verifyAccess(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  const verifyAccess = async (accessToken) => {
    setCheckingAccess(true);
    setError('');
    
    try {
      const response = await fetch(
        `${API_URL}/api/books/cire-mock-exam/check-access?token=${accessToken}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setHasAccess(true);
          setShowAccessForm(false);
          // Store token in localStorage
          localStorage.setItem('cire_mock_exam_token', accessToken);
          // Update URL with token
          setSearchParams({ token: accessToken });
        } else {
          setError('Invalid access code. Please check your email or purchase below.');
        }
      } else {
        setError('Unable to verify access. Please try again.');
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setError('Connection error. Please try again.');
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    
    if (!token) {
      setError('Please enter your access code.');
      return;
    }
    
    verifyAccess(token);
  };

  const handleExamClick = (examId) => {
    if (!hasAccess) return;
    const url = `/books/cire-mock-exams/${examId}?token=${tokenFromUrl || token}`;
    window.open(url, '_blank');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
    }}>
      {/* JSON-LD Structured Data for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "CIRE Mock Exam Pack",
          "description": "5 comprehensive CIRE mock exams with 550 practice questions. Prepare for the Canadian Investment Regulatory Examination with detailed rationales.",
          "provider": {
            "@type": "Organization",
            "name": "Vitta Finance Academy",
            "url": "https://www.vittafinance.ca"
          },
          "offers": {
            "@type": "Offer",
            "price": "49.99",
            "priceCurrency": "CAD",
            "availability": "https://schema.org/InStock",
            "url": "https://www.vittafinance.ca/books/cire-mock-exam-pack"
          },
          "educationalLevel": "Professional"
        })
      }} />
      
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #4f46e5 100%)',
        padding: '80px 20px 100px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Link to="/cire-exam" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: 'rgba(255,255,255,0.7)',
            textDecoration: 'none',
            fontSize: '14px',
            marginBottom: '24px'
          }}>
            ← Back to CIRE Exam
          </Link>
          
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 40px rgba(212,175,55,0.3)'
            }}>
              <Target size={40} color="white" strokeWidth={2.5} />
            </div>
            <div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(212,175,55,0.2)',
                border: '1px solid rgba(212,175,55,0.3)',
                borderRadius: '20px',
                padding: '6px 14px',
                marginBottom: '12px'
              }}>
                <Sparkles size={14} style={{ color: '#D4AF37' }} />
                <span style={{ color: '#D4AF37', fontSize: '12px', fontWeight: '600' }}>EXAM SIMULATION</span>
              </div>
              <h1 style={{
                fontSize: '42px',
                fontWeight: '800',
                color: 'white',
                lineHeight: 1.2,
                marginBottom: '12px'
              }}>
                CIRE Mock Exam Pack
              </h1>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255,255,255,0.8)',
                maxWidth: '600px',
                lineHeight: 1.6,
                marginBottom: '24px'
              }}>
                Test your readiness with 5 full-length CIRE mock exams. 550 questions total with comprehensive rationales for every question and answer.
              </p>
              
              {/* Pricing */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  fontSize: '2.8rem',
                  color: '#D4AF37',
                  fontWeight: 900,
                  lineHeight: 1
                }}>
                  ${PRICE} CAD
                </span>
                <div style={{
                  background: 'rgba(212,175,55,0.25)',
                  border: '2px solid rgba(212,175,55,0.5)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '0.9rem',
                  color: '#D4AF37',
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}>
                  Lifetime Access
                </div>
              </div>
            </div>
          </div>

          {checkingAccess && (
            <div style={{ color: 'rgba(255,255,255,0.6)' }}>Checking access...</div>
          )}
        </div>
      </div>

      {/* Sales Page - Show when no access and access form not shown */}
      {!hasAccess && !showAccessForm && (
        <div style={{
          maxWidth: '1200px',
          margin: '-60px auto 80px',
          padding: '0 20px',
          position: 'relative',
          zIndex: 2
        }}>
          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            marginBottom: '60px',
            flexWrap: 'wrap'
          }}>
            <a
              href={KAJABI_PURCHASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 36px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(212,175,55,0.4)',
                textDecoration: 'none',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <ShoppingCart size={22} />
              Buy Now - ${PRICE} CAD
            </a>
            <button
              onClick={() => setShowAccessForm(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 36px',
                background: 'rgba(255,255,255,0.1)',
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
              }}
            >
              <BookOpen size={22} />
              Already Purchased? Access Here
            </button>
          </div>

          {/* Features Section */}
          <div style={{
            background: 'rgba(30,41,59,0.6)',
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '40px',
            marginBottom: '40px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '800',
              color: 'white',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              What's Included
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>5 Full-Length Exams</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>550 comprehensive questions mirroring the real CIRE exam</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Detailed Rationales</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Comprehensive explanations for every question and answer</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Realistic Format</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Authentic exam experience with time tracking</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Practice & Exam Modes</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Study at your pace or simulate real exam conditions</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Element-by-Element Review</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Track your performance across all 9 CIRE elements</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <CheckCircle size={24} style={{ color: '#D4AF37', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'white', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Lifetime Access</h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>Practice as many times as you need</p>
                </div>
              </div>
            </div>
          </div>

          {/* Mock Exams Preview Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '24px'
          }}>
            {MOCK_EXAM_TILES.map((exam) => (
              <div
                key={exam.id}
                style={{
                  background: 'rgba(30,41,59,0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  padding: '28px',
                  opacity: 0.7,
                  position: 'relative'
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${exam.color}33, ${exam.color}22)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}>
                  <FileText size={30} color={exam.color} strokeWidth={2} />
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'white',
                  marginBottom: '12px'
                }}>
                  {exam.title}
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.6,
                  marginBottom: '16px'
                }}>
                  {exam.description}
                </p>
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(212,175,55,0.2)',
                  border: '1px solid rgba(212,175,55,0.4)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Lock size={14} style={{ color: '#D4AF37' }} />
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#D4AF37',
                    fontWeight: 600
                  }}>
                    Locked
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div style={{
            marginTop: '60px',
            textAlign: 'center',
            padding: '40px',
            background: 'rgba(30,41,59,0.6)',
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <h2 style={{
              fontSize: '32px',
              fontWeight: '800',
              color: 'white',
              marginBottom: '16px'
            }}>
              Ready to Test Your CIRE Knowledge?
            </h2>
            <p style={{
              fontSize: '18px',
              color: 'rgba(255,255,255,0.8)',
              marginBottom: '32px',
              lineHeight: 1.6
            }}>
              Get lifetime access to all 5 mock exams with comprehensive rationales
            </p>
            <a
              href={KAJABI_PURCHASE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '18px 40px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                fontSize: '18px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(212,175,55,0.3)',
                textDecoration: 'none'
              }}
            >
              <ShoppingCart size={22} />
              Get Instant Access - ${PRICE} CAD
            </a>
          </div>
        </div>
      )}

      {/* Access Form - Show when access button clicked */}
      {showAccessForm && !hasAccess && (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 20px' }}>
          <div style={{
            maxWidth: '500px',
            margin: '0 auto',
            background: 'rgba(30,41,59,0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '48px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 24px',
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2.5rem'
              }}>
                <Lock size={48} color="white" />
              </div>
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: 800,
                color: 'white',
                marginBottom: '12px'
              }}>
                Access Your Mock Exams
              </h2>
              <p style={{
                fontSize: '1rem',
                color: 'rgba(255,255,255,0.7)',
                lineHeight: 1.6
              }}>
                Enter your access code that was emailed to you after purchase
              </p>
            </div>

            <form onSubmit={handleAccessSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '8px'
                }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(15,23,42,0.6)',
                    color: 'white',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.9)',
                  marginBottom: '8px'
                }}>
                  Access Code
                </label>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Enter your access code"
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(15,23,42,0.6)',
                    color: 'white',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
              </div>

              {error && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '10px',
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  color: '#fca5a5',
                  fontSize: '0.9rem',
                  marginBottom: '20px'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={checkingAccess}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '12px',
                  border: 'none',
                  background: checkingAccess 
                    ? 'rgba(212,175,55,0.5)' 
                    : 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                  color: 'white',
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  cursor: checkingAccess ? 'not-allowed' : 'pointer'
                }}
              >
                {checkingAccess ? 'Verifying...' : 'Access Mock Exams'}
              </button>
            </form>

            <div style={{
              marginTop: '24px',
              textAlign: 'center',
              paddingTop: '24px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              <p style={{
                fontSize: '0.85rem',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '12px'
              }}>
                Don't have access yet?
              </p>
              <a
                href={KAJABI_PURCHASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#D4AF37',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 600
                }}
              >
                Purchase the Mock Exam Pack →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Mock Exams Grid - Show when has access */}
      {hasAccess && (
        <div style={{
          maxWidth: '1200px',
          margin: '-60px auto 80px',
          padding: '0 20px',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            background: 'rgba(34, 197, 94, 0.1)',
            border: '2px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '40px',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#22c55e',
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '8px'
            }}>
              Access Granted!
            </h3>
            <p style={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: '16px',
              margin: 0
            }}>
              You now have lifetime access to all 5 CIRE mock exams. Good luck with your preparation!
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '24px'
          }}>
            {MOCK_EXAM_TILES.map((exam) => (
              <div
                key={exam.id}
                onClick={() => handleExamClick(exam.id)}
                style={{
                  background: 'rgba(30,41,59,0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '20px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  padding: '28px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  position: 'relative'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(212,175,55,0.2)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${exam.color}33, ${exam.color}22)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}>
                  <FileText size={30} color={exam.color} strokeWidth={2} />
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'white',
                  marginBottom: '12px'
                }}>
                  {exam.title}
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: 'rgba(255,255,255,0.7)',
                  lineHeight: 1.6,
                  marginBottom: '16px'
                }}>
                  {exam.description}
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#D4AF37',
                  fontSize: '0.95rem',
                  fontWeight: 600
                }}>
                  Start Exam →
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CIREMockExamPackSalesPage;
